import { useCallback, useMemo } from 'react';
import { useObjectUrl } from '../../../hooks/useObjectUrl';
import { useGet } from '../../../hooks/useGet';
import { getFileNameFromContentDisposition } from './ExportDownload.utils';

const getFileNameWithoutExtension = (fileName: string, fileExtension: string): string => {
  const suffix = `.${fileExtension}`;
  if (fileName.toLowerCase().endsWith(suffix.toLowerCase())) {
    return fileName.slice(0, -suffix.length);
  }
  return fileName;
};

/**
 * Fetches the generated export file from the given URL, manages loading and error
 * state and provides an object URL for the downloaded file.
 */
export function useExportDownload(exportUrl: string | undefined, fileExtension: string) {
  const { state, response, requestError, loading, reset, get } = useGet<Blob>();
  const objectUrl = useObjectUrl(response?.data);

  const generateExport = useCallback(() => {
    if (!exportUrl) {
      return;
    }
    reset();
    get(exportUrl, {}, { responseType: 'blob' });
  }, [exportUrl, get, reset]);

  const fileName = useMemo(
    () => (response ? getFileNameFromContentDisposition(response.headers['content-disposition']) : undefined),
    [response]
  );

  const fileNameWithoutExtension = useMemo(
    () => (fileName ? getFileNameWithoutExtension(fileName, fileExtension) : undefined),
    [fileName, fileExtension]
  );

  return {
    state,
    response,
    requestError,
    loading,
    reset,
    get,
    objectUrl,
    fileName,
    fileNameWithoutExtension,
    generateExport,
  };
}
